import type { ReactNode } from "react";
import { AdminOrganizationsPage } from "./pages/AdminOrganizationsPage.tsx";
import { AnalyticsPage } from "./pages/AnalyticsPage.tsx";
import { BillingPage } from "./pages/BillingPage.tsx";
import { MembersPage } from "./pages/MembersPage.tsx";
import { OverviewPage } from "./pages/OverviewPage.tsx";
import { OrganizationsPage } from "./pages/OrganizationsPage.tsx";
import { PlatformAdminsPage } from "./pages/PlatformAdminsPage.tsx";
import { SettingsPage } from "./pages/SettingsPage.tsx";

export type PlatformRouteSection = "workspace" | "administration";

export interface PlatformRoute {
  readonly path: string;
  readonly label: string;
  readonly section: PlatformRouteSection;
  readonly requiresF5Admin?: boolean;
  readonly element: ReactNode;
}

function RoutePlaceholder({ title, section }: { readonly title: string; readonly section: string }): JSX.Element {
  const titleId = `platform-${title.toLowerCase().replaceAll(" ", "-")}-title`;
  return (
    <section className="platform-page" aria-labelledby={titleId}>
      <p className="platform-page__eyebrow">{section}</p>
      <h1 id={titleId}>{title}</h1>
    </section>
  );
}

/**
 * The shell owns routes while individual workspace pages remain independently
 * replaceable modules. The lightweight entries below are intentionally swapped
 * by their feature modules as those surfaces arrive.
 */
export const platformRoutes: readonly PlatformRoute[] = [
  {
    path: "/",
    label: "Overview",
    section: "workspace",
    element: <OverviewPage />,
  },
  {
    path: "/organizations",
    label: "Organizations",
    section: "workspace",
    element: <OrganizationsPage />,
  },
  {
    path: "/members",
    label: "Members",
    section: "workspace",
    element: <MembersPage />,
  },
  {
    path: "/billing",
    label: "Billing",
    section: "workspace",
    element: <BillingPage />,
  },
  {
    path: "/analytics",
    label: "Analytics",
    section: "workspace",
    element: <AnalyticsPage />,
  },
  {
    path: "/settings",
    label: "Settings",
    section: "workspace",
    element: <SettingsPage />,
  },
  {
    path: "/admin",
    label: "Administration",
    section: "administration",
    requiresF5Admin: true,
    element: <RoutePlaceholder title="Administration" section="Fulcrum5" />,
  },
  {
    path: "/admin/organizations",
    label: "Client organizations",
    section: "administration",
    requiresF5Admin: true,
    element: <AdminOrganizationsPage />,
  },
  {
    path: "/admin/platform-admins",
    label: "Platform admins",
    section: "administration",
    requiresF5Admin: true,
    element: <PlatformAdminsPage />,
  },
];

/** @deprecated Prefer `platformRoutes`; retained for existing workspace imports. */
export const organizationManagementRoutes = platformRoutes;
